/**
 * Session state.
 *
 * The access token lives in a ref here — in memory, never in storage — and is
 * handed to the HTTP client through `setAuthHelpers` rather than imported, so
 * `api/client.ts` stays free of React and independently testable.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { setAuthHelpers } from '@/api/client';
import type { CurrentUser } from '@/api/types';

import { authApi, type LoginPayload } from './api/authApi';
import { restoreSession } from './api/restore';
import { AuthContext, type AuthContextValue } from './authContext';

export function AuthProvider({ children }: { children: ReactNode }) {
  const accessToken = useRef<string | null>(null);
  const closing = useRef(false);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isRestoring, setIsRestoring] = useState(true);

  const clearSession = useCallback(() => {
    accessToken.current = null;
    setUser(null);
  }, []);

  /**
   * Leaving, in the only order that actually leaves.
   *
   * Both halves are required. The refresh cookie is HttpOnly, so `authApi.logout`
   * is the only thing in the whole app that can end the session on the server;
   * omitting it is what let a page reload resurrect a session the app had
   * already thrown away — the client forgot the user while the server still read
   * the cookie as live, and `restoreSession()` signed them straight back in.
   *
   * Local state goes first, deliberately: with no access token in memory the
   * logout rides out without an Authorization header, which is what lets the
   * server accept it whatever state its token was in.
   *
   * `swallow` is the difference between the two callers. A forced teardown has
   * nobody left to tell and the app is already logged out, so it stays quiet; an
   * explicit logout re-throws, because a logout that never reached the server
   * leaves a live cookie behind and the user deserves to know that.
   */
  const closeSession = useCallback(
    async (swallow: boolean) => {
      // The logout call goes out through the same client whose 401 handler is
      // what can call this. A teardown that re-enters itself is a loop.
      if (closing.current) return;
      closing.current = true;
      try {
        // No token in memory means there is no session to end. A cold start on
        // a logged-out browser reaches here through a `/auth/me/` 401 that
        // carried no Authorization header at all, and asking the server to close
        // a session that never existed would put a wasted request on every
        // anonymous page load.
        if (accessToken.current === null) return;
        clearSession();
        await authApi.logout();
      } catch (error) {
        if (!swallow) throw error;
      } finally {
        closing.current = false;
      }
    },
    [clearSession],
  );

  // Wire the HTTP client once, before anything can issue a request.
  useEffect(() => {
    setAuthHelpers({
      getToken: () => accessToken.current,
      onUnauthorized: () => void closeSession(true),
      onTokenRefreshed: (token) => {
        accessToken.current = token;
      },
    });
  }, [closeSession]);

  // Restore on load: the in-memory token did not survive the refresh, but the
  // HttpOnly cookie may have. `restoreSession` deduplicates the call across
  // the dev-mode StrictMode double-mount — without it, two `/auth/me/`
  // requests fire per page load (issue #18).
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const restored = await restoreSession();
      if (cancelled) return;
      if (restored) setUser(restored);
      else clearSession();
      setIsRestoring(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [clearSession]);

  const login = useCallback(async (payload: LoginPayload) => {
    const { access, user: loggedIn } = await authApi.login(payload);
    accessToken.current = access;
    setUser(loggedIn);
  }, []);

  const logout = useCallback(() => closeSession(false), [closeSession]);

  const refreshUser = useCallback(async () => {
    setUser(await authApi.me());
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isRestoring,
      login,
      logout,
      refreshUser,
    }),
    [user, isRestoring, login, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
